import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Check, Zap, Crown, Rocket } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const plans = [
  {
    name: "Avulso",
    description: "Pra quem quer testar antes de casar",
    price: "27,90",
    credits: 5,
    videos: 15,
    icon: Zap,
    features: [
      { name: "5 créditos (15 vídeos)", included: true },
      { name: "Créditos sem validade", included: true },
      { name: "Formato 9:16 otimizado", included: true },
      { name: "3 vídeos únicos por crédito", included: true },
      { name: "Download por 7 dias", included: true },
      { name: "Suporte por email", included: true },
    ],
    cta: "Quero Testar",
    ctaVariant: "outline" as const,
  },
  {
    name: "Mensal",
    description: "Pra quem quer escalar de verdade",
    price: "197,90",
    priceLabel: "/mês",
    credits: 30,
    videos: 90,
    icon: Crown,
    popular: true,
    features: [
      { name: "30 créditos/mês (90 vídeos)", included: true },
      { name: "Renovação automática", included: true },
      { name: "Formato 9:16 otimizado", included: true },
      { name: "3 vídeos únicos por crédito", included: true },
      { name: "Download por 7 dias", included: true },
      { name: "Suporte prioritário", included: true },
    ],
    cta: "Quero Escalar",
    ctaVariant: "hero" as const,
  },
  {
    name: "Anual",
    description: "Pra quem faz as contas direito",
    price: "1.997,90",
    priceLabel: "/ano",
    monthlyEquivalent: "166,49",
    credits: 360,
    videos: 1080,
    icon: Rocket,
    savings: "Economia de 16%",
    features: [
      { name: "360 créditos/ano (1080 vídeos)", included: true },
      { name: "30 créditos por mês", included: true },
      { name: "Formato 9:16 otimizado", included: true },
      { name: "3 vídeos únicos por crédito", included: true },
      { name: "Download por 7 dias", included: true },
      { name: "Suporte prioritário", included: true },
    ],
    cta: "Quero Economizar",
    ctaVariant: "hero" as const,
  },
];

const faqs = [
  {
    question: "Como funciona o sistema de créditos?",
    answer: "Cada crédito gera 1 task que cospe 3 vídeos UGC diferentes. Você manda a foto do produto, nome e descrição, e a gente faz a mágica: 3 roteiros únicos com vídeos prontos pra TikTok, Reels e Shorts.",
  },
  {
    question: "Tem plano grátis?",
    answer: "Não, mas por R$ 27,90 você testa 5 créditos e gera 15 vídeos. Se não gostar, a gente devolve (spoiler: você vai gostar).",
  },
  {
    question: "Por quanto tempo os vídeos ficam disponíveis?",
    answer: "7 dias após a conclusão da task. Baixa logo porque a gente não guarda. Não somos o Google Drive.",
  },
  {
    question: "Quais formas de pagamento?",
    answer: "Cartão de crédito, PIX e boleto. Tudo seguro, processado por parceiros confiáveis. Pode ir sem medo.",
  },
  {
    question: "Posso pedir reembolso?",
    answer: "Sim, até 7 dias após a compra. Tem limite de uso diário nesse período pra não ter espertinho, mas no geral é tranquilo.",
  },
  {
    question: "Os créditos avulsos expiram?",
    answer: "Não! Créditos avulsos são eternos (ou até você usar). Já os de assinatura renovam todo mês.",
  },
  {
    question: "E se uma task falhar?",
    answer: "Você reprocessa de graça. O sistema continua de onde parou, sem cobrar nada. A gente não lucra com erro.",
  },
];

export default function Precos() {
  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--yellow)/0.1),transparent_50%)]" />
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Preços que <span className="text-gradient">cabem no bolso</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Sem contrato de fidelidade. Sem surpresa na fatura. Cada crédito gera 3 vídeos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative rounded-2xl p-8 border transition-all duration-300 ${
                  plan.popular
                    ? "gradient-card border-primary glow-yellow scale-105"
                    : "bg-card border-border hover:border-primary/30"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full gradient-primary text-xs font-semibold text-primary-foreground">
                      <Crown className="w-3 h-3" />
                      Mais Popular
                    </span>
                  </div>
                )}
                {plan.savings && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full gradient-accent text-accent-foreground text-xs font-semibold">
                      {plan.savings}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-xl gradient-primary flex items-center justify-center mx-auto mb-4">
                    <plan.icon className="w-7 h-7 text-primary-foreground" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {plan.description}
                  </p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-sm text-muted-foreground">R$</span>
                    <span className="text-4xl font-bold">{plan.price}</span>
                    {plan.priceLabel && (
                      <span className="text-muted-foreground">{plan.priceLabel}</span>
                    )}
                  </div>
                  {plan.monthlyEquivalent && (
                    <p className="text-xs text-muted-foreground mt-1">
                      equivalente a R$ {plan.monthlyEquivalent}/mês
                    </p>
                  )}
                  <p className="text-sm text-primary mt-2 font-medium">
                    {plan.credits} créditos = {plan.videos} vídeos
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li
                      key={feature.name}
                      className="flex items-center gap-3 text-sm"
                    >
                      <Check className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground">{feature.name}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={plan.ctaVariant}
                  className="w-full"
                  size="lg"
                  asChild
                >
                  <Link to="/cadastro">{plan.cta}</Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">
              Perguntas <span className="text-gradient">Frequentes</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Tire suas dúvidas sobre o Publee e nossos planos.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-secondary/50 border border-border rounded-xl px-6 data-[state=open]:border-primary/30"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-5">
                    <span className="font-medium">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold mb-6">
              Bora criar <span className="text-gradient">publis</span>?
            </h2>
            <p className="text-muted-foreground mb-8">
              Comece com o pacote Avulso ou vá direto pro mensal e escale seus criativos.
            </p>
            <Button variant="hero" size="lg" asChild>
              <Link to="/cadastro">Começar Agora</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
